import React from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import InFeedAd from "@/components/ads/InFeedAd";
import WireSeoSections from "@/components/public/WireSeoSections";
import PublicBreadcrumbs from "@/components/public/PublicBreadcrumbs";
import WireHero from "@/components/public/WireHero";
import WireArticleCard from "@/components/public/WireArticleCard";
import { getWireCopy, getWireFallbackArticles } from "@/i18n";

export default function WireIndex() {
  const { lang: routeLang } = useParams();
  const queryLang = new URLSearchParams(window.location.search).get("lang");
  const lang = routeLang || queryLang || localStorage.getItem("appLanguage") || "en";
  const copy = getWireCopy(lang);
  const [searchQuery, setSearchQuery] = React.useState("");

  const { data: publishedArticles = [] } = useQuery({
    queryKey: ["wire-articles", lang],
    queryFn: () => base44.entities.WireArticle.filter({ status: "published", language: lang }, "-published_date", 100),
  });

  const articles = publishedArticles.length
    ? publishedArticles.map((article) => ({
        slug: article.slug,
        title: article.title,
        category: copy.categoryLabels?.[article.category] || article.category || copy.categoryLabels?.["voyage-planning"] || "voyage-planning",
        readTime: `${article.estimated_reading_time || 5} ${copy.min}`,
        excerpt: article.excerpt || article.meta_description,
        heroImage: article.hero_image,
      }))
    : getWireFallbackArticles(lang);

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const filteredArticles = normalizedQuery
    ? articles.filter((article) => [article.title, article.category, article.excerpt].join(" ").toLowerCase().includes(normalizedQuery))
    : articles;
  const featured = filteredArticles[0] || articles[0];
  const latestArticles = filteredArticles.filter((article) => article.slug !== featured?.slug);

  return (
    <div className="maritime-container py-8 md:py-14">
      <PublicBreadcrumbs items={[{ label: copy.home, href: `/${lang}` }, { label: copy.wire }]} />

      <WireHero
        copy={copy}
        lang={lang}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        articleCount={articles.length}
      />

      {featured && (
        <section id="latest-wire" className="mt-10 scroll-mt-24">
          <div className="mb-5 flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="text-sm font-black uppercase tracking-[0.22em] text-primary">{copy.startHere}</p>
              <h2 className="font-heading text-3xl font-black md:text-4xl">{copy.featuredTitle}</h2>
            </div>
            <p className="max-w-xl text-sm leading-6 text-muted-foreground">{copy.featuredIntro}</p>
          </div>
          <WireArticleCard article={featured} href={`/${lang}/wire/${featured.slug}`} featured label={copy.featured} readLabel={copy.readBriefing} openLabel={copy.open} />
        </section>
      )}

      <section className="mt-10">
        <div className="mb-5 flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-sm font-black uppercase tracking-[0.22em] text-primary">{copy.browse}</p>
            <h2 className="font-heading text-3xl font-black md:text-4xl">{copy.latestTitle}</h2>
            </div>
            <p className="text-sm font-bold text-muted-foreground">{filteredArticles.length} {copy.results}</p>
        </div>

        {filteredArticles.length ? (
          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {latestArticles.map((article, index) => (
              <React.Fragment key={article.slug}>
                <WireArticleCard article={article} href={`/${lang}/wire/${article.slug}`} readLabel={copy.readBriefing} openLabel={copy.open} />
                {index === 5 && <InFeedAd className="col-span-full" />}
              </React.Fragment>
            ))}
          </div>
        ) : (
          <div className="rounded-[2rem] border border-dashed border-border bg-card p-8 text-center">
            <h3 className="text-2xl font-black text-foreground">{copy.noFoundTitle}</h3>
            <p className="mt-2 text-muted-foreground">{copy.noFoundText}</p>
          </div>
        )}
      </section>

      <div id="wire-guide" className="scroll-mt-24">
        <WireSeoSections lang={lang} />
      </div>
    </div>
  );
}